import { css } from '@emotion/react';

import useTheme from '../../hooks/useTheme';

export default () => {
  const { spacing, colors, borderRadius, breakpoints } = useTheme();

  const getInputColors = (hasError, colors) => ({
    label: hasError ? colors.buttonRed : colors.white,

    border: hasError ? colors.buttonRed : colors.color_aebde6,

    text: hasError ? colors.black : colors.white,

    bg: hasError ? colors.pale_red : 'transparent',

    placeholder: hasError ? colors.placeholder_black2 : colors.white,

    hoverText: hasError ? colors.placeholder_black2 : colors.navy,

    hoverBg: hasError ? colors.pale_red : colors.white,
  });

  return {
    base: hasError => {
      const theme = getInputColors(hasError, colors);

      return css`
        display: flex;
        flex-direction: column;
        align-items: center;

        label {
          cursor: pointer;
          color: ${theme.label};
          margin-bottom: ${spacing.xs};
        }

        input {
          width: 100%;
          height: 3rem;
          font-family: 'FS Dillon';
          border: 2px solid ${theme.border};
          border-radius: ${hasError
            ? `${borderRadius.m} ${borderRadius.m} 0 0`
            : `${borderRadius.m}`};
          position: relative;
          z-index: 1;
          text-align: center;
          color: ${theme.text};
          outline: 0;
          font-weight: ${hasError ? 400 : 700};
          font-size: 1rem;
          background-color: ${theme.bg};

          /* Default placeholder color: white (unless error) */
          &::placeholder {
            color: ${theme.placeholder};
            text-align: center;
            font-size: 18px;
            font-weight: ${hasError ? 400 : 700};
            font-family: 'FS Dillon';
            opacity: 1;
          }

          &:hover {
            background-color: ${theme.hoverBg};
            border-color: ${hasError ? colors.buttonRed : colors.border_black};
            color: ${theme.hoverText};
          }
          &:hover::placeholder {
            color: ${theme.hoverText};
          }

          &:focus {
            color: ${theme.hoverText};
            background-color: ${theme.hoverBg};
            border-color: ${hasError
              ? colors.buttonRed
              : colors.border_black} !important;
          }
          &:focus::placeholder {
            color: ${theme.hoverText};
            opacity: 1;
          }

          &.keyboard-focus:focus {
            outline: 2px solid ${colors.skip_yellow};
            border: 2px solid ${colors.border_black} !important;
            border-radius: ${borderRadius.m};
          }

          &.mouse-focus:focus {
            outline: none;
            border: 2px solid ${colors.white};
            border-radius: ${hasError
              ? `${borderRadius.m} ${borderRadius.m} 0 0`
              : `${borderRadius.m}`};
          }
        }

        ${breakpoints.l} {
          align-items: start;
        }
      `;
    },
    inputWrapper: css`
      width: 327px;
      display: flex;
      flex-direction: column;
      align-items: center;

      ${breakpoints.l} {
        width: 391px;
        align-items: start;
      }
    `,
    answer: css`
      padding-bottom: 1.5rem;
      font-family: 'FS Dillon';
    `,
    ageYear: isSelected => css`
      font-family: 'FS Dillon';
      font-weight: 500;
      &:focus-visible {
        outline: none;
      }
      @media (hover: hover) and (pointer: fine) {
        &:hover {
          &::before {
            background-color: ${colors.white};
          }
          span {
            color: ${colors.navy};
          }
        }
      }
      ${breakpoints.xl} {
        &:hover {
          &::before {
            background-color: ${colors.white};
          }
          span {
            color: ${colors.navy};
          }
        }
      }

      ${isSelected &&
      css`
        &::before {
          background-color: ${colors.white};
        }

        span {
          color: ${colors.navy};
        }
      `}

      ${!isSelected &&
      css`
        svg {
          display: none;
        }
      `}

      ${breakpoints.l} {
        padding: ${spacing.m};

        &::before {
          background-size: 45%;
        }
      }
    `,
    buttonNext: (arrowContext, selectedYear, isMini) => css`
      font-family: 'FS Dillon';
      width: 327px;
      height: 48px;
      background-color: ${colors.red};
      color: ${colors.white};
      border: 1px ${colors.red} solid;
      border-radius: 0.25rem;
      margin-top: ${!isMini ? (selectedYear ? '0' : '22px') : '22px'};
      font-size: 1rem;

      ${arrowContext === false &&
      css`
        background-color: ${colors.border_grey};
        color: ${colors.next_grey};
        border: 1px ${colors.border_grey} solid;
        cursor: initial;
      `}

      ${arrowContext === true &&
      css`
        background-color: ${colors.red};
        color: ${colors.white};
        cursor: pointer;
      `}

      ${breakpoints.l} {
        width: 391px;
      }
      &:not(:disabled) {
        &:hover {
          background-color: ${colors.white} !important;
          color: #16161e !important;
          border: none;
        }
      }
      &:focus-visible {
        outline: none;
        color: ${colors.border_black};
        border: 2px solid ${colors.border_black};
        background-color: ${colors.skip_yellow};
        text-decoration: underline;
      }
    `,
    loader: id => css`
      margin-left: 0.25rem;
      text-align: center;
      align-self: center;

      svg {
        width: 100%;
        max-width: 85px;
        // animation: logo-animation 1000ms linear infinite normal forwards;
      }

      #${id} {
        animation: logo-animation 3000ms linear infinite normal forwards;
      }

      @keyframes logo-animation {
        0% {
          transform: translate(45.669999px, 46.839997px) rotate(0deg);
        }

        100% {
          transform: translate(45.669999px, 46.839997px) rotate(1800deg);
        }
      }
    `,
    helpText: css`
      color: ${colors.lightBlue};
      text-align: start;
      margin-top: 1rem;
      margin-bottom: 1rem;
      letter-spacing: 0.0027em;
      font-size: 18px;
      height: 1rem;
      margin-right: 12rem;

      ${breakpoints.l} {
        margin-right: 4rem;
        margin-bottom: 26px;
        text-align: left;
        font-size: 1.25rem;
      }
    `,
    errorMessage: validationError => css`
      display: flex;
      align-items: center;
      gap: 0.625rem;
      border-radius: 0 0 0.25rem 0.25rem;
      background: ${colors.buttonRed};
      color: #fff;
      line-height: 1.25rem;
      letter-spacing: 0.015rem;
      /* fill the parent wrapper width so it aligns with input/button */
      width: 100%;
      box-sizing: border-box;
      padding: 0.5rem;
      /* place behind the input's outline so focus ring remains visible */
      position: relative;
      z-index: 0;

      p {
        margin: 0;
      }
    `,
  };
};
